'use client';

import { Skeleton } from '@mui/material';

import { ChatContextButton } from '@/features/chat/components';
import { RetainerCycleSwitcher } from '@/ui/atomic-organisms';
import { Badge, SectionStepperTabs } from '@web/ui';

import styles from './JobPageHeader.module.scss';

import type { JobPageHeaderStepItem } from './JobPageHeader';
import type { JobStepId, JobSummaryDraft } from './new-job/types';
import type { JobStatus, RetainerCycle } from '@/features/jobs/types';

type JobHeaderContentProps = {
  summary: JobSummaryDraft;
  activeStepId: JobStepId;
  steps: JobPageHeaderStepItem[];
  onStepChange: (stepId: JobStepId) => void;
  showSteps?: boolean;
  retainerCycles?: RetainerCycle[];
  activeRetainerCycleId?: string | null;
  onRetainerCycleChange?: (cycleId: string) => void;
  isLoading?: boolean;
};

function getStatusTone(status: JobStatus) {
  if (status === 'active') return 'success';
  if (status === 'pending') return 'warning';
  if (status === 'completed') return 'info';
  if (status === 'archived' || status === 'archive') return 'default';
  return 'default';
}

function getClientLabel(summary: JobSummaryDraft) {
  if (summary.clientMode === 'internal') {
    return 'Internal';
  }

  const parts = [summary.company?.name, summary.person?.name].filter(Boolean);
  if (!parts.length) {
    return 'Client';
  }

  return `Client - ${parts.join(' - ')}`;
}

function getKindLabel(jobKind: JobSummaryDraft['jobKind']) {
  return jobKind === 'retainer' ? 'Retainer' : 'Project';
}

function getStatusLabel(status: JobStatus) {
  if (status === 'inprogress') return 'In Progress';
  if (status === 'archive') return 'Archived';
  return `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
}

export function JobHeaderContent({
  summary,
  activeStepId,
  steps,
  onStepChange,
  showSteps = true,
  retainerCycles = [],
  activeRetainerCycleId = null,
  onRetainerCycleChange,
  isLoading = false,
}: JobHeaderContentProps) {
  const resolvedTitle = summary.name.trim() || 'Untitled Job';
  const resolvedJobNumber = summary.jobNumber
    ? `Job #${summary.jobNumber}`
    : 'Job #***';
  const shouldShowChatButton =
    Boolean(summary.id) && summary.status !== 'pending';
  const shouldShowRetainerCycleSwitcher =
    summary.jobKind === 'retainer' &&
    retainerCycles.length > 0 &&
    Boolean(onRetainerCycleChange);

  if (isLoading) {
    return (
      <div className={styles.jobHeaderContent}>
        <Skeleton
          data-testid="job-header-content-loading"
          variant="rounded"
          width="100%"
          height={25}
        />
      </div>
    );
  }

  return (
    <div className={styles.jobHeaderContent}>
      <div className={styles.jobPageHeaderTop}>
        <div className={styles.jobPageHeaderTop__details}>
          <h2 className={styles.jobPageHeaderTop__title}>
            {resolvedTitle}&nbsp;&bull;
          </h2>
          <div className={styles.jobPageHeaderTop__meta}>
            <span>{resolvedJobNumber}&nbsp;&bull;</span>
            <span>{getKindLabel(summary.jobKind)}&nbsp;&bull;</span>
            <Badge
              label={getStatusLabel(summary.status)}
              tone={getStatusTone(summary.status)}
              size="lg"
            />
            <span>&bull;&nbsp;{getClientLabel(summary)}</span>
          </div>
        </div>
        {shouldShowRetainerCycleSwitcher ||
        (shouldShowChatButton && summary.id) ? (
          <div className={styles.jobPageHeaderTop__actions}>
            {shouldShowRetainerCycleSwitcher && onRetainerCycleChange ? (
              <RetainerCycleSwitcher
                cycles={retainerCycles}
                selectedCycleId={activeRetainerCycleId}
                onChange={onRetainerCycleChange}
              />
            ) : null}

            {shouldShowChatButton && summary.id ? (
              <ChatContextButton
                target={{
                  type: 'job',
                  id: summary.id,
                  label: resolvedTitle,
                  sourceJobId: summary.id,
                  sourceJobLabel: resolvedTitle,
                }}
                messageCount={summary.messageCount}
                unreadCount={summary.unreadCount}
                mentionCount={summary.mentionCount}
              />
            ) : null}
          </div>
        ) : null}
      </div>

      {showSteps ? (
        <SectionStepperTabs
          sections={steps}
          activeId={activeStepId}
          onSectionChange={(stepId) => onStepChange(stepId as JobStepId)}
          navigationMode="free"
          aria-label="Job steps"
        />
      ) : null}
    </div>
  );
}
